import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const DOCUMENT_TYPES = [
  { type: 'USER_TERMS', label: 'Általános Szerződési Feltételek (Felhasználók)' },
  { type: 'PROVIDER_TERMS', label: 'Partneri Általános Szerződési Feltételek' },
];

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function AdminLegalPage() {
  const [documents, setDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(null);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const entries = await Promise.all(
        DOCUMENT_TYPES.map(async ({ type }) => {
          const response = await apiService.get(API_ENDPOINTS.LEGAL.CURRENT(type));
          return [type, response.data.document];
        })
      );
      setDocuments(Object.fromEntries(entries));
    } catch {
      setError('Nem sikerült betölteni az ÁSZF dokumentumokat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handlePublish = async (documentId) => {
    setPublishing(documentId);
    setError('');
    setResult(null);
    try {
      const response = await apiService.post(API_ENDPOINTS.LEGAL.ADMIN_PUBLISH(documentId));
      setResult({ documentId, ...response.data });
      await fetchDocuments();
    } catch {
      setError('A kiküldés sikertelen volt.');
    } finally {
      setPublishing(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        A "Kiküldés" gomb minden érintett, aktív felhasználónak/partnernek elfogadandó bejegyzést hoz létre a jelenlegi ÁSZF-verzióhoz, és emailben + in-app értesítésben tájékoztatja őket. Ha 8 napon belül nem fogadják el, de a fiókjuk aktív marad, automatikusan elfogadottnak minősül. Új dokumentum-tartalom (pl. szövegmódosítás) a backend seed adatai közül, kódmódosítással kerül be — ez a gomb a már meglévő, jelenlegi verzió kiküldését indítja el a felhasználóbázisnak.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Kiküldve: {result.notifiedCount} érintett felhasználó/partner kapott elfogadandó bejegyzést.
          {result.emailFailedCount > 0 && ` (${result.emailFailedCount} email küldése sikertelen volt — az elfogadási határidő ettől függetlenül érvényes.)`}
        </Alert>
      )}

      <Stack spacing={2}>
        {DOCUMENT_TYPES.map(({ type, label }) => {
          const doc = documents[type];
          if (!doc) return null;
          return (
            <Card key={type} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>{label}</Typography>
                    <Typography variant="body2" color="text.secondary">Verzió: {doc.version}</Typography>
                    <Typography variant="body2" color="text.secondary">Hatályos: {formatDate(doc.effectiveAt)}</Typography>
                    <Box sx={{ mt: 1 }}>
                      {doc.publishedAt ? (
                        <Chip size="small" color="success" label={`Kiküldve: ${formatDate(doc.publishedAt)}`} />
                      ) : (
                        <Chip size="small" color="warning" label="Még nincs kiküldve" />
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={publishing === doc.id ? <CircularProgress size={16} color="inherit" /> : <Send />}
                    disabled={publishing === doc.id}
                    onClick={() => handlePublish(doc.id)}
                  >
                    Kiküldés
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        A kiküldés a jelenleg regisztrált, aktív felhasználókra/partnerekre vonatkozik. A gomb ismételt megnyomása nem küld duplán értesítést azoknak, akiknek már van elfogadandó bejegyzésük — csak az újonnan érintetteket (pl. azóta regisztráltakat, akik még nem fogadták el) jelöli meg.
      </Typography>
    </Box>
  );
}
